import type {CSSResult, PropertyDeclaration, PropertyDeclarations} from 'lit';
import {html, LitElement} from 'lit';
import {property} from 'lit/decorators.js';
import {createElement} from 'react';
import type {FunctionComponent} from 'react';
import type {Root} from 'react-dom/client';
import {createRoot} from 'react-dom/client';

import {defaultKilnPlugins} from './plugins/default.js';
import type {KilnPlugin} from './utils.js';

interface _KilnProperty<Values> extends PropertyDeclaration {
  default?: Values;
}

type KilnProperties<Props extends object> = {
  [Property in keyof Props]: PropertyDeclaration & {
    default?: Props[Property];
  };
};

abstract class KilnElementBase extends LitElement {
  abstract unmountReactRoot(): void;
  abstract reactRender(hotComponent?: unknown): void;
}

const isDev = process.env.NODE_ENV === 'development';
const kilnInstanceRegistry = new Map<string, KilnElementBase[]>();
const kilnTagRegistry = new Set<string>();

export function isKilnElement(element: Element): boolean {
  return (
    '__IS_KILN_ELEMENT__' in element && element.__IS_KILN_ELEMENT__ === true
  );
}

/**
 * Generates a LitElement component given a react component.
 */
export function kiln<
  ReactProps extends object,
  KilnProps extends object = ReactProps,
>(
  tagName: string,
  {
    component,
    styles,
    props: declarations,
    plugins = [],
  }: {
    component: FunctionComponent<ReactProps>;
    props: KilnProperties<KilnProps>;
    styles?: CSSResult[] | CSSResult;
    plugins?: KilnPlugin[];
  }
): typeof LitElement {
  // Defaults run last so their providers wrap component-specific plugin output.
  const resolvedPlugins = [...plugins, ...defaultKilnPlugins];

  class KilnElement extends KilnElementBase {
    @property({type: Boolean, attribute: true, reflect: true})
    island = false;

    @property({type: Boolean, attribute: true, reflect: true})
    shadow = !!styles;

    slotElements: Element[] = [];

    reactRoot?: Root;
    rootElement?: React.ReactNode;

    private readonly __IS_KILN_ELEMENT__ = true;

    static get properties(): PropertyDeclarations {
      return declarations as PropertyDeclarations;
    }

    static get styles() {
      if (!styles) return;
      return styles;
    }

    protected setDefaults() {
      for (const key in declarations) {
        const prop = declarations[key];
        if (prop.default) {
          (this as typeof declarations)[key] = prop.default;
        }
      }
    }

    protected generateProps(this: KilnElement): ReactProps {
      const props: Partial<ReactProps> = {};

      for (const key in declarations) {
        const kilnPropKey = key as keyof KilnElement;
        const propKey = key as unknown as keyof ReactProps;
        const propValue = this[
          kilnPropKey
        ] as unknown as ReactProps[keyof ReactProps];
        Object.assign(props, {[propKey]: propValue});
      }

      return props as ReactProps;
    }

    constructor() {
      super();
      this.setDefaults();
      kilnInstanceRegistry.set(tagName, [
        ...(kilnInstanceRegistry.get(tagName) ?? []),
        this,
      ]);
    }

    connectedCallback(): void {
      super.connectedCallback();

      this.reactRender();

      if (this.island) {
        this.island = true;
      }
    }

    disconnectedCallback() {
      if (isDev) {
        const instances = kilnInstanceRegistry.get(tagName);
        if (instances) {
          const updatedInstances = instances.filter(inst => inst !== this);
          kilnInstanceRegistry.set(tagName, updatedInstances);
        }
      }
      // Defer unmount so we don't tear down during a parent React Router render.
      queueMicrotask(() => {
        if (this.isConnected) return;

        this.reactRoot?.unmount();
        this.reactRoot = undefined;
        this.rootElement = undefined;
      });
      resolvedPlugins.forEach(p => p.disconnect?.(this));
      super.disconnectedCallback();
    }

    updated(changedProperties: Map<string, unknown>) {
      super.updated(changedProperties);
      this.reactRender();
    }

    protected createRenderRoot(): Element | ShadowRoot {
      if (this.shadow) return super.createRenderRoot();
      return this;
    }

    protected gatherChildren(children: NodeListOf<ChildNode> | HTMLCollection) {
      const reactChildren: React.ReactNode[] = [];

      for (const node of children) {
        if (node instanceof HTMLSlotElement) {
          continue;
        }

        if (node instanceof Element && isKilnElement(node)) {
          const kilnElement = node as KilnElement;
          kilnElement.reactRender();
          if (kilnElement.rootElement) {
            reactChildren.push(kilnElement.rootElement);
          }
          continue;
        }

        /*
        if (node instanceof LitElement) {
          // FIXME(joshua): Get this working
          const _component = createComponent({
            tagName: node.tagName,
            elementClass: node.constructor as any,
            react: React as any,
          })
          continue;
        }*/

        if (node instanceof Text) {
          reactChildren.push(node.textContent);
          continue;
        }

        if (node instanceof Element && !isKilnElement(node)) {
          reactChildren.push(
            createElement(
              node.tagName.toLowerCase(),
              {key: Math.random()},
              node.childNodes.length > 0
                ? this.gatherChildren(node.childNodes)
                : node.textContent
            )
          );
          continue;
        }
      }

      return reactChildren;
    }

    unmountReactRoot() {
      this.reactRoot?.unmount();
      this.reactRoot = undefined;
      this.rootElement = undefined;
    }

    reactRender(hotComponent?: FunctionComponent<ReactProps>) {
      if (!this.isConnected && !hotComponent) return;

      const rootComponent = hotComponent ?? component;

      if (!this.reactRoot || hotComponent) {
        this.reactRoot = createRoot(this.renderRoot);
      }
      resolvedPlugins.forEach(p => p.beforeRender?.(this));

      const root = createElement(
        rootComponent,
        this.generateProps(),
        this.gatherChildren(this.childNodes)
      );

      this.rootElement = resolvedPlugins
        .filter(plugin => !!plugin.render)
        .map(plugin => plugin.render!)
        .reduce((r: React.ReactNode, fn) => fn(r, this), root);

      this.reactRoot.render(this.rootElement);
      this.island = false;

      resolvedPlugins.forEach(p => p.afterRender?.(this));
    }

    render() {
      return html``;
    }
  }

  /**
   * Register the custom element if it hasn't been registered yet.
   * If it has been registered, re-render the React component.
   * This is useful for hot module reloading.
   */
  if (!customElements.get(tagName)) {
    customElements.define(tagName, KilnElement);
  } else if (isDev) {
    kilnInstanceRegistry.get(tagName)?.forEach(instance => {
      instance.unmountReactRoot();
      instance.reactRender(component);
    });
  }

  return KilnElement;
}

export const ignite = (additionalTags?: string[] | Set<string>) => {
  if (document) {
    const ready = (() => {
      let fired = false;

      return async () => {
        if (fired) return;

        if (kilnTagRegistry.size === 0) {
          document.body.classList.add('kiln-ready');
          fired = true;
          return;
        }

        try {
          await Promise.allSettled(
            [...kilnTagRegistry, ...(additionalTags ?? [])].map(
              customElements.whenDefined
            )
          );
        } catch (err) {
          console.error(err);
        } finally {
          document.body.classList.add('kiln-ready');
          fired = true;
        }
      };
    })();

    if (
      document.readyState === 'interactive' ||
      document.readyState === 'complete'
    ) {
      ready();
    } else {
      document.onreadystatechange = async () => ready();
    }
  }
};
